import PropTypes from "prop-types";

import "./browsersupport.scss";
import { TYPES } from "./constants";
import PartialSupportedBrowserVersion from "./PartialSupportedBrowserVersion";
import UnknownBrowser from "./UnknownBrowser";
import UnsupportedBrowser from "./UnsupportedBrowser";
import UnsupportedBrowserVersion from "./UnsupportedBrowserVersion";
import { getBrowserStatus } from "./utils";

const BrowserSupport = ({
  overrides = {},
  unsupportedBrowsers = [],
  children,
}) => {
  const {
    browserName,
    browserVersion,
    browserType,
    deviceType,
    isUnsupportedBrowser,
    isKnownBrowser,
    isBrowserFullySupported,
    isBrowserPartiallySupported,
    requiredMinBrowserVersion,
    downloadPageLink,
  } = getBrowserStatus(overrides, unsupportedBrowsers);

  if (browserType === TYPES.INAPP || deviceType === TYPES.MOBILE) {
    return <>{children}</>;
  }

  if (isUnsupportedBrowser) {
    return <UnsupportedBrowser {...{ browserName, browserVersion }} />;
  }

  if (!isKnownBrowser) {
    return (
      <UnknownBrowser {...{ browserName, browserVersion }}>
        {children}
      </UnknownBrowser>
    );
  }

  if (isBrowserFullySupported) return <>{children}</>;

  if (isBrowserPartiallySupported) {
    return (
      <PartialSupportedBrowserVersion
        {...{
          browserName,
          browserVersion,
          downloadPageLink,
          requiredMinBrowserVersion,
        }}
      >
        {children}
      </PartialSupportedBrowserVersion>
    );
  }

  return (
    <UnsupportedBrowserVersion
      {...{
        browserName,
        browserVersion,
        downloadPageLink,
        requiredMinBrowserVersion,
      }}
    />
  );
};

BrowserSupport.propTypes = {
  /**
   * To override the default minimum supported versions of the browsers.
   */
  overrides: PropTypes.shape({
    Chrome: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    Edge: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    Firefox: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    IE: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    Opera: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    Safari: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    AndroidBrowser: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    OperaMini: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    MobileChrome: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    MobileFirefox: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    UCBrowser: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
    SamsungInternet: PropTypes.shape({
      hardLimit: PropTypes.number,
      softLimit: PropTypes.number,
    }),
  }),
  /**
   * To add more browsers to the list of unsupported browsers
   */
  unsupportedBrowsers: PropTypes.arrayOf(PropTypes.string),
};

export default BrowserSupport;
